import { FCC, Id } from "@dgs/core";
import { Day, StartOfWeekOptions, endOfMonth, endOfWeek, startOfMonth, startOfWeek } from "date-fns";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

export type CalendarState = {
	selectedDate: Date;
	startOfMonthAndWeek: Date;
	endOfMonthAndWeek: Date;
	settings: CalendarSettings;
	weekOptions: StartOfWeekOptions<Date>;
	setSelectedDate: (date: Date) => void;
	setSearch: (search: string) => void;
};
export type Weekday = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

const startOfWeekMap: Record<Weekday, number> = {
	monday: 1,
	tuesday: 2,
	wednesday: 3,
	thursday: 4,
	friday: 5,
	saturday: 6,
	sunday: 0,
};

type CalendarSettings = {
	showCalendarWeeks: boolean;
	showHolidays: boolean;
	startDayOfTheWeek: Weekday;
	yasumiProviderId: Id;
};

const CalendarStateContext = createContext<CalendarState | undefined>(undefined);

interface Props {
	settings: CalendarSettings;
}

export const CalendarProvider: FCC<Props> = ({ children, settings }) => {
	const [state, setState] = useState({
		search: "",
		selectedDate: new Date(),
	});
	const weekOptions = useMemo(
		() => ({ weekStartsOn: startOfWeekMap[settings.startDayOfTheWeek] as Day }),
		[settings.startDayOfTheWeek],
	);
	const value = {
		...state,
		settings,
		weekOptions,
		startOfMonthAndWeek: useMemo(
			() => startOfWeek(startOfMonth(state.selectedDate), weekOptions),
			[state.selectedDate, weekOptions],
		),
		endOfMonthAndWeek: useMemo(
			() => endOfWeek(endOfMonth(state.selectedDate), weekOptions),
			[state.selectedDate, weekOptions],
		),
		setSearch: useCallback(
			(search: string) =>
				setState((x) => ({
					...x,
					search,
				})),
			[],
		),
		setSelectedDate: useCallback(
			(selectedDate: Date) =>
				setState((x) => ({
					...x,
					selectedDate,
				})),
			[],
		),
	};

	return <CalendarStateContext.Provider value={value}>{children}</CalendarStateContext.Provider>;
};

export const useCalendarProvider = () => {
	const ctx = useContext(CalendarStateContext);

	if (ctx === undefined) {
		throw new Error("CalendarStateContext undefined. Did you forget to wrap your components in CalendarProvider?");
	}

	return ctx;
};
